/**
 * Balão de passo e anel em volta do elemento que o guia aponta (protótipo do Claude Design,
 * 28/10/2026). O balão é escuro (`--dica`), com setinha; o anel é tinta e branco, nunca amarelo,
 * porque o alvo muitas vezes é a ação amarela da tela. Os dois seguem o elemento a cada quadro e
 * somem quando ele sai da tela; o balão troca de lado quando não cabe.
 *
 * Movimento: o anel entra com fade e solta um halo uma vez (M12 b); o balão entra a partir da
 * setinha, 180 ms depois. Com "reduzir movimento", só fade de 120 ms (guia.css).
 * O balão não prende o foco: a pessoa continua usando a tela. O título é o nome do diálogo.
 */
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { acharAncora, posicionar, type Caixa, type Lado } from './ancora';

export interface BalaoDef {
  /** Seletor do elemento apontado. */
  alvo: string;
  lado: Lado;
  titulo: string;
  texto: string;
  /** "Entendi", nos passos de leitura. */
  entendi?: { rotulo: string; acao: () => void };
  largura?: number;
  /** Só o anel, sem balão (o cartão já diz o que fazer). */
  semBalao?: boolean;
}

interface Medida {
  caixa: Caixa;
  raio: number;
}

const PADDING = 4;

function medir(el: HTMLElement): Medida {
  const r = el.getBoundingClientRect();
  const raio = Number.parseFloat(getComputedStyle(el).borderTopLeftRadius) || 8;
  return { caixa: { x: r.left, y: r.top, w: r.width, h: r.height }, raio };
}

function igual(a: Medida | null, b: Medida | null): boolean {
  if (!a || !b) return a === b;
  return (
    Math.abs(a.caixa.x - b.caixa.x) < 0.5 &&
    Math.abs(a.caixa.y - b.caixa.y) < 0.5 &&
    Math.abs(a.caixa.w - b.caixa.w) < 0.5 &&
    Math.abs(a.caixa.h - b.caixa.h) < 0.5
  );
}

/** Acompanha o elemento a cada quadro (ele anda com a rolagem e com as janelas que abrem). */
function useMedida(seletor: string): Medida | null {
  const [medida, setMedida] = useState<Medida | null>(null);
  const ultima = useRef<Medida | null>(null);
  const rolou = useRef(false);
  useEffect(() => {
    rolou.current = false;
    let quadro = 0;
    const passo = (): void => {
      const el = acharAncora(seletor);
      const nova = el ? medir(el) : null;
      if (el && nova && !rolou.current) {
        rolou.current = true;
        const foraDaTela = nova.caixa.y < 0 || nova.caixa.y + nova.caixa.h > window.innerHeight;
        if (foraDaTela) el.scrollIntoView({ block: 'center' });
      }
      if (!igual(nova, ultima.current)) {
        ultima.current = nova;
        setMedida(nova);
      }
      quadro = window.requestAnimationFrame(passo);
    };
    quadro = window.requestAnimationFrame(passo);
    return () => window.cancelAnimationFrame(quadro);
  }, [seletor]);
  return medida;
}

export function GuiaBalao({ def }: { def: BalaoDef }): JSX.Element | null {
  const medida = useMedida(def.alvo);
  const balaoRef = useRef<HTMLDivElement | null>(null);
  const [tamanho, setTamanho] = useState({ w: def.largura ?? 300, h: 120 });
  const tituloId = `gp-balao-${def.alvo.replace(/[^a-z0-9]/gi, '')}`;

  useLayoutEffect(() => {
    const el = balaoRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    if (Math.abs(r.width - tamanho.w) > 0.5 || Math.abs(r.height - tamanho.h) > 0.5) {
      setTamanho({ w: r.width, h: r.height });
    }
  }, [medida, def.titulo, def.texto, def.largura, tamanho.w, tamanho.h]);

  if (!medida) return null;
  const alvo: Caixa = {
    x: medida.caixa.x - PADDING,
    y: medida.caixa.y - PADDING,
    w: medida.caixa.w + PADDING * 2,
    h: medida.caixa.h + PADDING * 2,
  };
  const tela = { w: window.innerWidth, h: window.innerHeight };
  const pos = posicionar(alvo, def.lado, tamanho, tela);
  const desloca = {
    esq: ['6px', '0px'],
    dir: ['-6px', '0px'],
    cima: ['0px', '6px'],
    baixo: ['0px', '-6px'],
  }[pos.lado];

  return createPortal(
    <div className="gp-camada">
      <div
        className="gp-anel"
        aria-hidden="true"
        style={{
          left: alvo.x,
          top: alvo.y,
          width: alvo.w,
          height: alvo.h,
          borderRadius: medida.raio + PADDING,
        }}
      />
      {def.semBalao ? null : (
        <div
          ref={balaoRef}
          className="gp-balao"
          role="dialog"
          aria-labelledby={tituloId}
          style={
            {
              left: pos.x,
              top: pos.y,
              width: def.largura ?? 300,
              '--gp-dx': desloca[0],
              '--gp-dy': desloca[1],
            } as CSSProperties
          }
        >
          <span
            className="gp-balao__seta"
            aria-hidden="true"
            style={{ left: pos.setaX - 7, top: pos.setaY - 7 }}
          />
          <b id={tituloId}>{def.titulo}</b>
          <p>{def.texto}</p>
          {def.entendi ? (
            <div className="gp-balao__acao">
              <button type="button" onClick={def.entendi.acao}>
                {def.entendi.rotulo}
              </button>
            </div>
          ) : null}
        </div>
      )}
    </div>,
    document.body,
  );
}
